import { z } from 'zod';
import { UuidSchema } from './common';
import { RequestStatusSchema, RequestTypeSchema } from './enums';

/**
 * §14 ТЗ. Показатели по запускам опросов.
 *
 * Живут отдельно от дашборда, потому что считаются в двух местах: на дашборде
 * (§14) и в сводке раздела «Результаты опросов» (§13). Определение «доли
 * ответивших» и «средней оценки» обязано быть одно на весь сервис — две копии
 * разошлись бы на первой же правке, и цифры в соседних разделах перестали бы
 * сходиться.
 */

export const SurveyCountsSchema = z.object({
  scheduled: z.number().int().min(0),
  sent: z.number().int().min(0),
  inProgress: z.number().int().min(0),
  completed: z.number().int().min(0),
  expired: z.number().int().min(0),
  cancelled: z.number().int().min(0),
  total: z.number().int().min(0),
  /**
   * Доля ответивших: завершённые к разосланным. Запланированные, но ещё
   * не отправленные, в знаменатель не идут — человек их не получал.
   * null, когда рассылать было нечего: 0 % и «нет данных» — разные вещи.
   */
  responseRate: z.number().min(0).max(1).nullable(),
});
export type SurveyCounts = z.infer<typeof SurveyCountsSchema>;

export const RatingBucketSchema = z.object({
  value: z.number().int().min(1).max(5),
  count: z.number().int().min(0),
});
export type RatingBucket = z.infer<typeof RatingBucketSchema>;

/** Шаг динамики: единая календарная сетка, подобранная под ширину отбора. */
export const TrendStepSchema = z.enum(['DAY', 'WEEK', 'MONTH']);
export type TrendStep = z.infer<typeof TrendStepSchema>;

/**
 * Точка динамики — календарная корзина по дню отправки запусков.
 *
 * Ключ периода §9.2 на ось не выходит: это понятие планировщика («один запуск
 * на период»), а не ось времени. Точки по ключам смешивали три формата подписей
 * и накрывали одни и те же дни дважды — недельная W34 рядом с дневными
 * 19–21.08 (QA 22.08.2026, дефект 2). Ответ считается в корзине того дня,
 * когда его опрос был отправлен; когда человек фактически ответил — на точку
 * не влияет.
 */
export const RatingTrendPointSchema = z.object({
  /** первый день корзины (UTC), 'ГГГГ-ММ-ДД' */
  bucket: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  average: z.number(),
  answers: z.number().int().min(0),
  completedRuns: z.number().int().min(0),
});
export type RatingTrendPoint = z.infer<typeof RatingTrendPointSchema>;

export const RatingsSchema = z.object({
  average: z.number().nullable(),
  answers: z.number().int().min(0),
  /** всегда пять корзин, включая нулевые: график не должен «схлопываться» */
  distribution: z.array(RatingBucketSchema).length(5),
  /** одинаков для всех точек ответа: смешанной сетки на одной оси не бывает */
  trendStep: TrendStepSchema,
  trend: z.array(RatingTrendPointSchema),
});
export type Ratings = z.infer<typeof RatingsSchema>;

export const IncidentCountsSchema = z.object({
  new: z.number().int().min(0),
  inProgress: z.number().int().min(0),
  closed: z.number().int().min(0),
  rejected: z.number().int().min(0),
  /** открытые = новые + в работе; §14 просит именно эту цифру отдельно */
  open: z.number().int().min(0),
  total: z.number().int().min(0),
});
export type IncidentCounts = z.infer<typeof IncidentCountsSchema>;

/** Разрез по объектам: строка таблицы на дашборде и столбец диаграммы в результатах. */
export const ProjectMetricsSchema = z.object({
  projectId: UuidSchema,
  projectName: z.string(),
  timezone: z.string(),
  runs: z.number().int().min(0),
  completed: z.number().int().min(0),
  expired: z.number().int().min(0),
  responseRate: z.number().min(0).max(1).nullable(),
  average: z.number().nullable(),
  incidents: z.number().int().min(0),
  openIncidents: z.number().int().min(0),
});
export type ProjectMetrics = z.infer<typeof ProjectMetricsSchema>;

// ───────────────────────────── Обращения ─────────────────────────────
//
// Счётчики лежат здесь, а не в схеме дашборда: те же разрезы показывает сводка
// раздела «Обращения» (§13), и определение «открытого» обязано быть одно.

export const RequestTypeCountsSchema = z.object({
  type: RequestTypeSchema,
  total: z.number().int().min(0),
  open: z.number().int().min(0),
});
export type RequestTypeCounts = z.infer<typeof RequestTypeCountsSchema>;

export const RequestStatusCountsSchema = z.object({
  status: RequestStatusSchema,
  count: z.number().int().min(0),
});
export type RequestStatusCounts = z.infer<typeof RequestStatusCountsSchema>;
